import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, FileText, Mail, ShieldCheck } from "lucide-react";
import { REVEAL_VIEW } from "@/lib/motionPresets";

const GOLD = "#C9A84C";
export const CARTE_STRIPE_LINK = "https://buy.stripe.com/5kQfZhaJydDEfnugMD0sU0i";
export const CARTE_EXEMPLE_PDF = "https://customer-assets-cm19k8pv.emergentagent.net/wingman/56c934d3-821c-4fb8-a48b-2263548aae70/attachments/6186f34b7bbd49e09754d8f0f3fb6c52_Carte_du_Decideur_Exemple_Marc_DUVAL.pdf";
export const CARTE_META = {
  title: "La Carte du Décideur | Académie AETHERIS",
  description: "La Carte du Décideur : une lecture Triple Fusion™ (Pythagore, Lo Shu, Ifà) de votre manière de décider. Document personnalisé de 15 pages, livré par email sous 5 jours. 197 €.",
};

const Reveal = ({ children, delay = 0 }) => (
  <motion.div {...REVEAL_VIEW} transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}>{children}</motion.div>
);

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const POINTS = [
  "Votre Chemin de Vie et les appuis naturels de votre manière de trancher.",
  "Les équilibres et les absences de votre grille Lo Shu, lus sous l'angle de la décision.",
  "Une perspective stratégique issue de l'Ifà, reliée à la question professionnelle que vous formulerez.",
  "Une synthèse : où vous êtes constant, où vous hésitez, et une ou deux pistes concrètes à confronter à votre réalité.",
];

export default function CarteDuDecideur() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = CARTE_META.title;
    setMeta("name", "description", CARTE_META.description);
    setMeta("property", "og:title", CARTE_META.title);
    setMeta("property", "og:description", CARTE_META.description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", "https://serenite-essence.fr/carte-du-decideur");
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="min-h-screen bg-brand-ink text-white" data-testid="carte-decideur-page">
      <section className="pt-36 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-6" style={{ color: GOLD }}>Lecture Triple Fusion™ pour dirigeants · 197 €</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-8" data-testid="carte-title">
              La Carte du <span style={{ color: GOLD }}>Décideur</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed mb-6">
              Chaque dirigeant décide avec une signature qui lui est propre : rapide ou mesurée, solitaire ou concertée, constante ou changeante.
              La Carte du Décideur est une lecture Triple Fusion™ (Pythagore, Lo Shu, Ifà) de votre manière de décider.
            </p>
            <p className="text-base text-white/60 font-light leading-relaxed mb-10">
              Un document personnalisé de 15 pages, rédigé par Rodrigue CASSILDÉ à partir de votre date de naissance, de votre nom et d'une question professionnelle
              que vous formulez. Il vous est livré par email, en PDF, sous 5 jours. Cette lecture éclaire sans prédire : elle sert d'aide à la décision, la décision reste la vôtre.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <a href={CARTE_STRIPE_LINK} target="_blank" rel="noopener noreferrer" data-testid="carte-cta-stripe"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: GOLD }}>
                Recevoir ma Carte <ArrowRight size={16} />
              </a>
              <a href={CARTE_EXEMPLE_PDF} target="_blank" rel="noopener noreferrer" data-testid="carte-exemple-link"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-white/70 hover:text-white transition-colors underline underline-offset-4">
                <FileText size={15} /> Voir un exemple <ExternalLink size={13} />
              </a>
            </div>
            <p className="mt-3 text-xs text-white/40" data-testid="carte-exemple-note">L'exemple présente un profil fictif (Marc DUVAL), établi à des fins de démonstration.</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto border-t border-white/10 pt-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Le document</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-white mb-10">Ce que contient votre Carte</h2>
            <ul className="space-y-5" data-testid="carte-points">
              {POINTS.map((p) => (
                <li key={p} className="flex gap-4 text-white/75 font-light leading-relaxed">
                  <span className="mt-2 w-1.5 h-1.5 shrink-0" style={{ backgroundColor: GOLD }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 px-6">
        <div className="max-w-4xl mx-auto border-t border-white/10 pt-16 grid md:grid-cols-2 gap-10">
          <Reveal>
            <div className="flex items-start gap-4">
              <Mail size={20} strokeWidth={1.5} className="shrink-0 mt-1" style={{ color: GOLD }} />
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Déroulement</p>
                <p className="text-white/75 font-light leading-relaxed">
                  Après votre commande, vous recevez par email un court questionnaire (nom, date de naissance, question professionnelle).
                  Votre Carte vous est envoyée en PDF sous 5 jours à l'adresse indiquée. Prix unique : 197 €.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex items-start gap-4">
              <ShieldCheck size={20} strokeWidth={1.5} className="shrink-0 mt-1" style={{ color: GOLD }} />
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Droit de rétractation</p>
                <p className="text-white/60 font-light leading-relaxed text-sm" data-testid="carte-retractation">
                  La Carte du Décideur est un contenu numérique personnalisé, réalisé selon vos informations. En commandant, vous demandez son exécution
                  immédiate et reconnaissez renoncer à votre droit de rétractation (article L. 221-28 du Code de la consommation).
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
